import { apiClient, API_ENDPOINTS } from '../config';
import {
  ApiResponse,
  PaginatedResponse,
  Content,
  UploadRequest,
  UploadResponse,
  UploadProgress,
  ListContentRequest,
  RecentUpload,
} from '../types';

export class ContentService {
  // List content with pagination and filtering
  static async listContent(params: ListContentRequest = {}): Promise<PaginatedResponse<Content>> {
    // Build query params, converting tag_ids array to comma-separated string
    const queryParams: Record<string, any> = {
      page: params.page || 1,
      size: params.size || 25,
      sort_field: params.sort?.field,
      sort_direction: params.sort?.direction,
    };

    // Add filters
    if (params.filters) {
      const { tag_ids, ...otherFilters } = params.filters;
      Object.assign(queryParams, otherFilters);
      // Convert tag_ids array to comma-separated string
      if (tag_ids && tag_ids.length > 0) {
        queryParams.tag_ids = tag_ids.join(',');
      }
    }

    const response = await apiClient.get<PaginatedResponse<Content>>(API_ENDPOINTS.content.list, {
      params: queryParams,
    });
    return response.data;
  }

  // Get recent uploads for dashboard
  static async getRecentContent(limit: number = 10): Promise<RecentUpload[]> {
    const response = await apiClient.get<{ data: RecentUpload[] }>(API_ENDPOINTS.content.recent, {
      params: { limit },
    });
    return response.data.data;
  }

  // Get single content item
  static async getContent(id: string): Promise<Content> {
    const response = await apiClient.get<ApiResponse<Content>>(API_ENDPOINTS.content.detail(id));
    return response.data.data;
  }

  // Upload content with progress tracking
  static async uploadContent(
    uploadRequest: UploadRequest,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', uploadRequest.file);

    if (uploadRequest.title) formData.append('title', uploadRequest.title);
    if (uploadRequest.artist) formData.append('artist', uploadRequest.artist);
    if (uploadRequest.album) formData.append('album', uploadRequest.album);
    if (uploadRequest.genre) formData.append('genre', uploadRequest.genre);
    if (uploadRequest.category) formData.append('category', uploadRequest.category);
    if (uploadRequest.tags) {
      uploadRequest.tags.forEach(tag => formData.append('tags', tag));
    }
    // AI content flags
    if (uploadRequest.is_ai_generated !== undefined) {
      formData.append('is_ai_generated', uploadRequest.is_ai_generated ? 'true' : 'false');
    }
    if (uploadRequest.register_to_bucket !== undefined) {
      formData.append('register_to_bucket', uploadRequest.register_to_bucket ? 'true' : 'false');
    }

    const response = await apiClient.post<ApiResponse<UploadResponse>>(
      API_ENDPOINTS.content.upload,
      formData,
      {
        timeout: 600000, // 10 minutes for large file uploads (up to 2GB)
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress: UploadProgress = {
              loaded: progressEvent.loaded,
              total: progressEvent.total,
              percentage: Math.round((progressEvent.loaded * 100) / progressEvent.total),
            };

            // Calculate speed and time remaining
            const timeElapsed = Date.now() - (progressEvent as any).startTime || Date.now();
            if (timeElapsed > 1000) {
              progress.speed = progressEvent.loaded / (timeElapsed / 1000);
              progress.timeRemaining = (progressEvent.total - progressEvent.loaded) / progress.speed;
            }

            onProgress(progress);
          }
        },
      }
    );

    return response.data.data;
  }

  // Update content metadata
  static async updateContent(id: string, updates: Partial<Content>): Promise<Content> {
    const response = await apiClient.put<ApiResponse<Content>>(
      API_ENDPOINTS.content.detail(id),
      updates
    );
    return response.data.data;
  }

  // Delete content
  static async deleteContent(id: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.content.delete(id));
  }

  // Approve content
  static async approveContent(id: string, comment?: string): Promise<Content> {
    const response = await apiClient.post<ApiResponse<Content>>(
      API_ENDPOINTS.content.approve(id),
      { comment }
    );
    return response.data.data;
  }

  // Reject content
  static async rejectContent(id: string, reason: string): Promise<Content> {
    const response = await apiClient.post<ApiResponse<Content>>(
      API_ENDPOINTS.content.reject(id),
      { reason }
    );
    return response.data.data;
  }

  // Get content preview URL
  static getPreviewUrl(id: string): string {
    return `${apiClient.defaults.baseURL}${API_ENDPOINTS.content.preview(id)}`;
  }

  // Get content stream URL
  static getStreamUrl(id: string): string {
    const token = localStorage.getItem('access_token');
    const baseUrl = `${apiClient.defaults.baseURL}${API_ENDPOINTS.content.stream(id)}`;
    return token ? `${baseUrl}?token=${token}` : baseUrl;
  }

  // Get content download URL
  static getDownloadUrl(id: string): string {
    const token = localStorage.getItem('access_token');
    const baseUrl = `${apiClient.defaults.baseURL}${API_ENDPOINTS.content.download(id)}`;
    return token ? `${baseUrl}?token=${token}` : baseUrl;
  }

  // Get content metadata
  static async getMetadata(id: string): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>(API_ENDPOINTS.content.metadata(id));
    return response.data.data;
  }

  // Bulk operations
  static async bulkApprove(ids: string[]): Promise<void> {
    await apiClient.post('/api/content/bulk/approve', { content_ids: ids });
  }

  static async bulkReject(ids: string[], reason: string): Promise<void> {
    await apiClient.post('/api/content/bulk/reject', { content_ids: ids, reason });
  }

  static async bulkDelete(ids: string[]): Promise<void> {
    await apiClient.post('/api/content/bulk/delete', { content_ids: ids });
  }

  // Search suggestions
  static async getSearchSuggestions(query: string): Promise<string[]> {
    const response = await apiClient.get<ApiResponse<string[]>>('/api/content/search/suggestions', {
      params: { q: query },
    });
    return response.data.data;
  }

  // Content statistics
  static async getContentStats(): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>('/api/content/stats');
    return response.data.data;
  }

  // Reprocess content - modes: 'id3', 'identify', 'audio', 'full'
  static async reprocessContent(
    id: string,
    mode: 'id3' | 'identify' | 'audio' | 'full' = 'full'
  ): Promise<{ job_id: string; status: string }> {
    const response = await apiClient.post<ApiResponse<{ job_id: string; status: string }>>(
      `/api/content/${id}/reprocess`,
      { mode }
    );
    return response.data.data;
  }

  // Bulk reprocess content
  static async bulkReprocess(
    contentIds: string[],
    mode: 'id3' | 'identify' | 'audio' | 'full' = 'full'
  ): Promise<{ total: number; successful: number; failed: number }> {
    const response = await apiClient.post<ApiResponse<{ total: number; successful: number; failed: number }>>(
      '/api/content/bulk/reprocess',
      { content_ids: contentIds, mode }
    );
    return response.data.data;
  }

  // Upload artwork for content
  static async uploadArtwork(id: string, file: File): Promise<{ artwork_r2_key: string }> {
    const formData = new FormData();
    formData.append('artwork', file);

    const response = await apiClient.post<ApiResponse<{ artwork_r2_key: string }>>(
      `/api/content/${id}/artwork`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  }

  // Get artwork URL for content
  static getArtworkUrl(id: string): string {
    return `${apiClient.defaults.baseURL}/api/content/${id}/artwork`;
  }

  // Delete artwork for content
  static async deleteArtwork(id: string): Promise<void> {
    await apiClient.delete(`/api/content/${id}/artwork`);
  }
}